import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { colors, css, fonts, glyphs, useMediaQuery, getGraphColor } from "../../Styles";
import { Button } from "..";
import { ButtonTypes } from "../../Definitions";
import { LineSegment, VictoryArea, VictoryAxis, VictoryChart, VictoryContainer, VictoryLabel, VictoryLine, VictoryStack } from "victory";
import { linearProjection } from "./linearProjection";
import { FeedCurve } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { ComponentAmount, getMixtureDataPerComponent } from "./getMixtureDataPerComponent";
import { cloneDeep } from "lodash";

// const CHART_WIDTH = 550
// const CHART_HEIGHT = 300

interface FeedCurveViewerProps {
  data: FeedCurve
  isMiniature?: boolean
  showComponentLabels?: boolean
  showMilestoneLabels?: boolean
  showMilestoneLabelsBox?: boolean
  invertedColors?: boolean
  feedCurveWidth?: string
}

const constructDataMap = (feedCurve: FeedCurve) => {
  let firstDay = 0;
  let lastDay = 0;
  let lastDayTracker = 0;
  let feedDayTracker = 0;
  const milestonesFirstDay: number[] = [];
  const dataMap = new Map<number, ComponentAmount[]>(feedCurve.components?.map((component) => ([component.componentId, []])));
  feedCurve.milestones?.sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex-milestoneB.milestoneIndex)).forEach((milestone, milestoneIndex) => {
    lastDayTracker = milestoneIndex === 0 ? feedDayTracker : feedDayTracker+1;
    if (milestoneIndex !== 0) {
      milestonesFirstDay.push(lastDayTracker);
    }
    milestone.feedDays?.forEach((feedDay, dayIndex) => {
      if (milestoneIndex === 0 && dayIndex === 0) {
        firstDay = feedDay.day;
        milestonesFirstDay.push(firstDay);
      }
      feedDayTracker = (feedDay.day + lastDayTracker);
      dataMap.forEach((componentData, componentId) => {
        const dayComponent = feedDay.dayComponents.find((dayComponent) => dayComponent.componentId === componentId);
        if (componentData) {
          componentData.push({day: feedDay.day+lastDayTracker, amount: dayComponent ? dayComponent.amount/1000 : 0});
          lastDay = feedDay.day+lastDayTracker;
        }
      })
    } )
  });
  // }, [] as number[]));
  const totalAmounts = Array.from(dataMap.values()).reduce((acc,array) => {
    array.forEach((value, index) => {
      if (acc[index]) {
        acc[index] += value.amount
        return;
      }
      acc[index] = value.amount;
    })
    return acc
  }, [] as number[])

  if (totalAmounts.length === 0) {
    totalAmounts[0] = 0;
  }
  const maxAmount = Math.max(...totalAmounts);
  const minAmount = Math.min(...totalAmounts);
  return {dataMap, lastDay, firstDay, maxAmount , minAmount, milestonesFirstDay}
}

export default function FeedCurveViewer({ data: feedCurve, isMiniature = false, showComponentLabels = false, showMilestoneLabels = false, showMilestoneLabelsBox = false, invertedColors= false, feedCurveWidth }: FeedCurveViewerProps) {
  const { t } = useTranslation();
  const [hoveredComponent, setHoveredComponent] = useState<number | null>(null);
  const [showMilestonesBtn, setShowMilestonesBtn] = useState(false);
  
  const {dataMap, lastDay, firstDay, maxAmount, minAmount, milestonesFirstDay} = constructDataMap(cloneDeep(feedCurve));

  const amountOfDays = lastDay - firstDay;
  
  const hideMilestones = useMediaQuery(`(max-width: 1265px)`);
  const extendedMaxAmount = maxAmount * 1.2;

  const handleComponentHover = (componentNo: number | null) => {
    setHoveredComponent(componentNo);
  };

  const _feedCurveWidth = isMiniature ? "300px" : (feedCurveWidth || "600px");

  const victoryArea = useMemo(() => {
    const barData = getMixtureDataPerComponent(dataMap, linearProjection, amountOfDays);
    return Array.from(dataMap.keys()).map((id, index) => {
              return (
                <VictoryArea
                  interpolation={amountOfDays > 250 ? "linear" : "stepAfter"}
                {...(!isMiniature && showComponentLabels && {
                  events: [{
                    target: "data",
                    eventHandlers: {
                      onMouseEnter: () => {
                        setHoveredComponent(id);
                      },
                      onMouseLeave: () => {
                        setHoveredComponent(null);
                      },
                    },
                  }],
                })}
                  key={index}
                  data={barData.get(id)}
                  x="x"
                  y="y"
                  style={{
                    data: {
                      fill: hoveredComponent === id ? colors.greyDarkPrimary : getGraphColor(id),
                      stroke: "none",
                    },
                  }}
                />
              );
            })
  }, [amountOfDays, dataMap, hoveredComponent, isMiniature, showComponentLabels])

  return (
    <>
    {/* <div style={{ width: !isMiniature ? "600px" : "300px", position: "relative"}}> */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px"}}>          
        <div style={{ width: _feedCurveWidth, margin: "0 auto"}} >
        <VictoryChart width={550} height={300} containerComponent={<VictoryContainer width={550} height={300} />}>
          <VictoryStack>
            {victoryArea}
          </VictoryStack>

          {/* X-Axis: Days */}
          <VictoryAxis
            tickFormat={(x: any) => `${x}`}
            standalone={false}
            tickCount={!isMiniature ?  Math.min(10, amountOfDays) : Math.min(5, amountOfDays)}
            domain={[firstDay, lastDay+1]}
            style={{
              ticks: { stroke: colors.greyPrimary, size: 5 },
              axis: { stroke: colors.greyPrimary, strokeWidth: !isMiniature ? 2 : 1 }
            }}
            crossAxis={false}
            label={t("units.days", {defaultValue: "Days"})}
            tickLabelComponent={ // axis-values (numbers)
              <VictoryLabel
                dy={-3}
                style={{ ...fonts.paragraph,
                  ...(!isMiniature ? fonts.fontSize4 : fonts.fontSize1),
                  fill: colors.greyPrimary,
                }}
              />
            }
            axisLabelComponent={ // axis-header
              <VictoryLabel
                dy={-13}
                dx={!isMiniature ? 250 : 207}
                style={{ ...fonts.header,
                  ...(!isMiniature ? fonts.fontSize4 : fonts.fontSize1),
                  fill: colors.greyPrimary,
                }}
              />
            }
            axisComponent={<LineSegment x1={35} x2={538} />}
          />
          {/* Y-Axis: Amount */}
          <VictoryAxis
            dependentAxis
            domain={[minAmount, extendedMaxAmount]}
            tickFormat={(y: any) => `${y}`}
            standalone={false}
            tickCount={4}
            style={{
              ticks: { stroke: colors.greyPrimary, size: 5 },
              axis: { stroke: colors.greyPrimary, strokeWidth: !isMiniature ? 2 : 1 },
            }}
            offsetX={!isMiniature ? 40 : 40}
            crossAxis={false}
            label={t("units.kg", {defaultValue: "KG"})}
            tickLabelComponent={ // axis-values (numbers)
              <VictoryLabel
                style={{ ...fonts.paragraph,
                  ...(!isMiniature ? fonts.fontSize4 : fonts.fontSize1),
                  fill: colors.greyPrimary,
                }}
              />
            }
            axisLabelComponent={ // axis-header
              <VictoryLabel
                angle={0}
                dy={!isMiniature ? -115 : -115}
                dx={!isMiniature ? 4 : 0}
                style={{ ...fonts.header,
                  ...(!isMiniature ? fonts.fontSize4 : fonts.fontSize1),
                  fill: colors.greyPrimary,
                }}
              />
            }
            axisComponent={<LineSegment y1={!isMiniature ? 26 : 24} y2={!isMiniature ? 255 : 255} />}
          />


          {/* Milestone in the feedCurve, add a VictoryAxis where milestone startDay is offsetX */}
          {!isMiniature && (milestonesFirstDay.length ?? 0) > 0 && milestonesFirstDay.map((firstMilestoneDay, index) => {
            return (
              <VictoryLine
                width={550}
                height={300}
                key={index}
                data={[
                  { x: firstMilestoneDay, y: 0 },
                  { x: firstMilestoneDay, y: extendedMaxAmount, label: `m${index + 1}`},
                ]}
                domain={{
                  x: [firstDay, lastDay+1],
                  y: [0, extendedMaxAmount]
                }}
                style={{
                  data: { 
                    stroke: invertedColors ? colors.greyLightTertiary : colors.blackPrimary,
                    strokeWidth: 1, 
                    strokeDasharray: 2 
                  }
                }}
                scale={{ x: "linear", y: "linear" }}
                standalone={false}
                labels={({ datum }) => datum.label}
                labelComponent={
                  <VictoryLabel  
                    dy={13}
                    dx={3}
                    style={{...fonts.header,
                      ...fonts.fontSize5,
                      fill: invertedColors ? colors.greyLightTertiary : colors.blackPrimary,
                    }}
                    textAnchor="start"
                  />
                }
              />
            )
          })}
        </VictoryChart>
        </div>

        {/* COMPONENTS LABELS */}
        {showComponentLabels?
          <div style={{ marginTop: "-20px"}}>
            <div style={{ display: "flex", flexDirection: "row", gap: "10px", whiteSpace: "nowrap"}}>
              { feedCurve?.components?.map((component, index) => (
                <div key={index} 
                  onMouseEnter={() => handleComponentHover(component.componentId)}
                  onMouseLeave={() => handleComponentHover(null)}
                  style={{ 
                    cursor: "default",
                    outline: hoveredComponent === component.componentId ? "2px solid "+colors.greyPrimary : "none",
                    outlineOffset: hoveredComponent === component.componentId ? "2px" : "none",
                    borderRadius: "5px"
                  }}
                > 
                  <div key={index} style={{ 
                    filter: hoveredComponent === component.componentId ? "opacity(0.7)" : "opacity(1)",
                    transition: "0.3s",
                    backgroundColor: getGraphColor(component.componentId), 
                    ...css.colorTag, 
                    ...fonts.header, 
                    ...fonts.fontSize4, 
                    color: colors.whitePrimary }}
                  >
                    {component.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        : <></> }

        {/* MILESTONES LABELS */}
        {(showMilestoneLabelsBox && hideMilestones)? ( <>
          { showMilestonesBtn ? ( <>
              <div onClick={() => setShowMilestonesBtn(false)} style={{ cursor: "pointer", position: "absolute", top: "120px", right: "20px" }}>
                <glyphs.Close size="25px"/>
              </div>
              <div style={{minWidth: "130px", position: "absolute", top: "150px", right: "25px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d", borderRadius: "10px"}}>
                <div style={{ backgroundColor: colors.greyDarkPrimary, borderRadius: "8px 8px 0px 0px", padding: "7px", textAlign: "center", ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>
                  {t("feedCurvePages.feedCurve.milestones", {defaultValue: "Milestones"})}
                </div>
                <div style={{backgroundColor: colors.greyDarkSecondary, borderRadius: "0px 0px 8px 8px"}}>
                  <div style={{
                    height: "fit-content",
                    borderRadius: "0px 0px 8px 8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                    justifyContent: "center",
                    border: "3px solid "+colors.greyDarkPrimary,
                    borderTop: "none",
                    padding: "10px"
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        {[...(feedCurve.milestones ?? [])].sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex-milestoneB.milestoneIndex))?.map((milestone, index) => (
                          <div key={index} style={{ color: colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4, lineHeight: "22px" }}>
                            {`M${index + 1}: ${milestone.name}`}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </> ) : (
              <div style={{position: "absolute", top: "120px", right: "20px"}}>
                <Button 
                  content={t("feedCurvePages.feedCurve.milestones", {defaultValue: "Milestones"})} 
                  onClick={() => setShowMilestonesBtn(true)}
                  buttonType={ButtonTypes.textButton}
                  startEnhancer={glyphs.Animal({size: "25px"})}
                /> 
              </div>
            )}
          </>

        ) :(showMilestoneLabelsBox && !hideMilestones)? (
          <div style={{minWidth: "130px", position: "absolute", top: "184px", right: "41px" }}>
            <div style={{ backgroundColor: colors.greyDarkPrimary, borderRadius: "8px 8px 0px 0px", padding: "7px", textAlign: "center", ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>
              {t("feedCurvePages.feedCurve.milestones", {defaultValue: "Milestones"})}
            </div>
            <div style={{backgroundColor: colors.greyDarkSecondary, borderRadius: "0px 0px 8px 8px",}}>
              <div style={{
                height: "fit-content",
                borderRadius: "0px 0px 8px 8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                justifyContent: "center",
                border: "3px solid "+colors.greyDarkPrimary,
                borderTop: "none",
                padding: "10px"
              }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    {[...(feedCurve.milestones ?? [])].sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex-milestoneB.milestoneIndex))?.map((milestone, index) => (
                      <div key={index} style={{ color: colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4, lineHeight: "22px" }}>
                        {`M${index + 1}: ${milestone.name}`}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div> 

        ) : showMilestoneLabels?
          <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: "20px" }}>
          {feedCurve.milestones?.map((milestone, index) => (
            <div key={index} style={{ color: colors.greyDarkPrimary, ...fonts.header, ...fonts.fontSize4 }}>
              {`M${index + 1}: ${milestone.name}`}
            </div>
          ))}
          </div>
        : <></> }

      </div>
    </>
  );
}
